import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useCopyToClipboard,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime } from '../lib/format';

interface PackBuild {
  id: string;
  packId: string;
  toolset: string | null;
  clientApp: string | null;
  frameworks: string[];
  checksum: string;
  secretScanPassed: boolean;
  createdBy: string | null;
  createdAt: string;
  expiresAt: string;
  fileCount: number;
}

export function IntegrationPacksPage({ role }: { role: string }) {
  const builds = useResource<{ items: PackBuild[] }>('/api/admin/integration-packs');
  const apps = useResource<{ items: Array<{ id: string; key: string }> }>('/api/admin/client-apps');
  const toolsets = useResource<{ items: Array<{ id: string; key: string }> }>(
    '/api/admin/mcp-toolsets',
  );
  const toast = useToast();
  const [copied, copy] = useCopyToClipboard();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    clientAppId: '',
    toolsetId: '',
    workloadEnvVar: 'API_HUB_KEY',
    devTokenEnvVar: 'API_HUB_DEV_TOKEN',
  });
  const [viewing, setViewing] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  const detail = useResource<{
    files: Record<string, string>;
    checksum: string;
    secretScanPassed: boolean;
  }>(viewing ? `/api/admin/integration-packs/${viewing}` : null);
  const files = detail.data?.files ?? {};
  const fileNames = Object.keys(files).sort();
  const currentFile = selectedFile ?? fileNames[0] ?? null;

  return (
    <>
      <PageHeader
        title="Pakiety integracyjne"
        description="Gotowe do skopiowania pliki dla Codex, Claude Code i zwykłych klientów. Zawierają wyłącznie nazwy zmiennych środowiskowych — nigdy sekretów."
        actions={
          canWrite ? (
            <Button variant="primary" onClick={() => setOpen(true)}>
              Wygeneruj pakiet
            </Button>
          ) : undefined
        }
      />

      <Callout>
        Każdy pakiet przechodzi skanowanie w poszukiwaniu wartości wyglądających na sekrety.
        Generowanie zostaje przerwane, jeśli cokolwiek zostanie znalezione.
      </Callout>

      {builds.loading ? (
        <Skeleton rows={4} />
      ) : (
        <Card tight>
          <DataTable
            rows={builds.data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak pakietów"
                description="Wygeneruj pakiet, aby podłączyć nowy projekt."
              />
            }
            columns={[
              {
                key: 'pack',
                header: 'Pakiet',
                render: (row) => <span className="ah-mono">{row.packId}</span>,
              },
              { key: 'app', header: 'Aplikacja', render: (row) => row.clientApp ?? '—' },
              { key: 'toolset', header: 'Zestaw', render: (row) => row.toolset ?? '—' },
              { key: 'files', header: 'Plików', align: 'right', render: (row) => row.fileCount },
              {
                key: 'scan',
                header: 'Skan sekretów',
                render: (row) => (
                  <Badge tone={row.secretScanPassed ? 'ok' : 'danger'}>
                    {row.secretScanPassed ? 'czysty' : 'ODRZUCONY'}
                  </Badge>
                ),
              },
              {
                key: 'created',
                header: 'Utworzono',
                render: (row) => formatDateTime(row.createdAt),
              },
              { key: 'expires', header: 'Wygasa', render: (row) => formatDateTime(row.expiresAt) },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <Button
                    size="sm"
                    onClick={() => {
                      setViewing(row.id);
                      setSelectedFile(null);
                    }}
                  >
                    Otwórz
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title="Wygeneruj pakiet integracyjny"
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.clientAppId && !form.toolsetId}
              onClick={async () => {
                try {
                  const result = await api.post<{ id: string; secretScanPassed: boolean }>(
                    '/api/admin/integration-packs',
                    {
                      ...(form.clientAppId ? { clientAppId: form.clientAppId } : {}),
                      ...(form.toolsetId ? { toolsetId: form.toolsetId } : {}),
                      workloadEnvVar: form.workloadEnvVar,
                      devTokenEnvVar: form.devTokenEnvVar,
                    },
                  );
                  toast.success(
                    'Pakiet wygenerowany',
                    result.secretScanPassed
                      ? 'Skan sekretów: czysty'
                      : 'Uwaga: skan wykrył problem',
                  );
                  setOpen(false);
                  setViewing(result.id);
                  setSelectedFile(null);
                  builds.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Generuj
            </Button>
          </>
        }
      >
        <Field label="Aplikacja kliencka">
          {(id) => (
            <Select
              id={id}
              value={form.clientAppId}
              onChange={(event) => setForm({ ...form, clientAppId: event.target.value })}
            >
              <option value="">— brak —</option>
              {(apps.data?.items ?? []).map((app) => (
                <option key={app.id} value={app.id}>
                  {app.key}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Zestaw narzędzi MCP" hint="Wyznacza listę operacji w manifeście i w OpenAPI.">
          {(id) => (
            <Select
              id={id}
              value={form.toolsetId}
              onChange={(event) => setForm({ ...form, toolsetId: event.target.value })}
            >
              <option value="">— brak —</option>
              {(toolsets.data?.items ?? []).map((toolset) => (
                <option key={toolset.id} value={toolset.id}>
                  {toolset.key}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <div className="ah-grid cols-2">
          <Field label="Zmienna klucza aplikacji">
            {(id) => (
              <TextInput
                id={id}
                value={form.workloadEnvVar}
                onChange={(event) => setForm({ ...form, workloadEnvVar: event.target.value })}
              />
            )}
          </Field>
          <Field label="Zmienna tokenu deweloperskiego">
            {(id) => (
              <TextInput
                id={id}
                value={form.devTokenEnvVar}
                onChange={(event) => setForm({ ...form, devTokenEnvVar: event.target.value })}
              />
            )}
          </Field>
        </div>
      </Modal>

      <Modal
        open={viewing !== null}
        onOpenChange={(open2) => !open2 && setViewing(null)}
        title="Zawartość pakietu"
        description={detail.data ? `Suma kontrolna ${detail.data.checksum.slice(0, 16)}…` : ''}
      >
        {detail.loading && <Skeleton rows={6} />}
        {detail.data && (
          <>
            <Badge tone={detail.data.secretScanPassed ? 'ok' : 'danger'}>
              skan sekretów: {detail.data.secretScanPassed ? 'czysty' : 'ODRZUCONY'}
            </Badge>
            <Field label="Plik">
              {(id) => (
                <Select
                  id={id}
                  value={currentFile ?? ''}
                  onChange={(event) => setSelectedFile(event.target.value)}
                >
                  {fileNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {currentFile && (
              <>
                <pre className="ah-code">{files[currentFile]}</pre>
                <Button onClick={() => copy(files[currentFile] ?? '')}>
                  {copied ? 'Skopiowano' : 'Kopiuj plik'}
                </Button>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
